"use client";

import { useEffect, useSyncExternalStore } from "react";
import {
  getAuthUserServerSnapshot,
  getAuthUserSnapshot,
  subscribeAuthUser,
  writeCachedAuthUser,
  type CachedAuthUser,
} from "./clientAuthCache";

/**
 * Resolves nav auth state without waiting on a fresh server round trip.
 * `serverUser` is what SiteNav resolved for THIS render (undefined while its
 * Suspense fallback is showing, null/user once fetchMe settles). Login and
 * logout write the resolved user straight into the sessionStorage-backed
 * store (see clientAuthCache) so every mounted nav in the tab picks it up
 * via useSyncExternalStore, immediately — no dependency on router.refresh()
 * timing or which routes it does/doesn't invalidate, and no wait on a fresh
 * RSC fetch for a route that already has an answer cached in this tab.
 *
 * The server render stays the source of truth: whenever `serverUser`
 * resolves to a definite value it's returned directly and re-synced into the
 * cache, so a genuinely expired session — or the first-ever load in a tab
 * with nothing cached yet — still gets the correct answer.
 *
 * Only writes when the resolved value actually differs from what's already
 * cached — a live QA crash traced back to this: `writeCachedAuthUser`
 * broadcasts by calling every registered listener in `clientAuthCache`'s
 * subscriber `Set` synchronously, which forces every other mounted nav's
 * `useSyncExternalStore` subscriber to re-render immediately. Firing that
 * unconditionally on every render whose server
 * value happens to be defined meant a session invalidating between one
 * navigation and the next (confirmed via Django logs: a stale session
 * cookie surviving a QA database reset, now getting a real 403 from
 * `/v1/users/me/`) forced a synchronous cross-component re-render in the
 * middle of React committing the newly-navigated page — a plausible cause
 * of the reproducible `removeChild`/`NotFoundError` crash on first
 * navigation into `/privacy` and `/blog`. A real login/logout still updates
 * every mounted nav instantly; a same-valued re-resolution (the overwhelming
 * majority of renders) is now a no-op instead of a forced broadcast.
 */
export function useAuthUser(serverUser: CachedAuthUser | undefined): CachedAuthUser | undefined {
  const cached = useSyncExternalStore(
    subscribeAuthUser,
    getAuthUserSnapshot,
    getAuthUserServerSnapshot,
  );

  useEffect(() => {
    if (serverUser === undefined) return;
    const current = getAuthUserSnapshot();
    if (JSON.stringify(current) === JSON.stringify(serverUser)) return;
    writeCachedAuthUser(serverUser);
  }, [serverUser]);

  return serverUser !== undefined ? serverUser : cached;
}
